import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { suppliersAPI, scopesAPI, filesAPI, auditAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, Edit, Trash2, Building2, MapPin, Phone, Mail, 
  Calendar, CheckCircle, XCircle, FileText, Upload, Download,
  AlertCircle, Clock, RefreshCw, Star, TrendingUp, ChevronDown, ChevronUp, History
} from 'lucide-react';
import { toast } from 'sonner';
import { SupplierPerformanceCard } from '@/components/SupplierPerformanceCard';

const STATUS_STYLES = {
  'Created': 'bg-slate-100 text-slate-600 border-slate-200',
  'Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'On Hold': 'bg-amber-50 text-amber-700 border-amber-200',
  'No Longer Needed': 'bg-red-50 text-red-700 border-red-200',
  'Under re-evaluation': 'bg-blue-50 text-blue-700 border-blue-200',
};

const CRITICALITY_LABELS = {
  special_process: 'Special process', class1_pse: 'Class 1 / PSE part', raw_material: 'Raw material',
  standard_commercial: 'Standard or commercial item', other: 'Other',
};
const RISK_LEVEL_STYLES = { low: 'bg-emerald-50 text-emerald-700 border-emerald-200', medium: 'bg-amber-50 text-amber-700 border-amber-200', high: 'bg-red-50 text-red-700 border-red-200' };

function RiskAssessmentRows({ ev }) {
  if (!ev?.product_process_criticality && !ev?.customer_approval_required && !ev?.overall_risk_level) return null;
  return (
    <>
      {ev.product_process_criticality && (
        <div><span className="text-slate-400">Criticality of product / process:</span> <span className="text-slate-700">{CRITICALITY_LABELS[ev.product_process_criticality] || ev.product_process_criticality}{ev.product_process_criticality === 'other' && ev.product_process_criticality_other ? ` — ${ev.product_process_criticality_other}` : ''}</span></div>
      )}
      {ev.customer_approval_required && (
        <div><span className="text-slate-400">Customer approval required:</span> <span className="text-slate-700">{ev.customer_approval_required === 'yes' ? 'Yes' : 'No'}{ev.customer_approval_reference ? ` — ${ev.customer_approval_reference}` : ''}</span></div>
      )}
      {ev.overall_risk_level && (
        <div>
          <span className="text-slate-400">Overall supplier risk:</span>{' '}
          <Badge variant="outline" className={`text-[10px] ml-1 ${RISK_LEVEL_STYLES[ev.overall_risk_level] || ''}`}>{ev.overall_risk_level}</Badge>
          {['medium', 'high'].includes(ev.overall_risk_level) && (
            <span className="block text-[10px] text-amber-700 mt-0.5">Controls to be selected before first order as per QSP-MT-840</span>
          )}
        </div>
      )}
    </>
  );
}

function RatingGauge({ label, value, max = 100 }) {
  if (value == null) return null;
  const pct = Math.min((value / max) * 100, 100);
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div data-testid={`rating-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        <span className="text-sm font-bold text-[#0F172A]">{value}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function SupplierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [supplier, setSupplier] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [startingReeval, setStartingReeval] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [changeLogExpanded, setChangeLogExpanded] = useState(false);

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';
  const isQualityManager = isAdmin || user?.role === 'quality_manager';

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [supplierRes, scopesRes, filesRes, auditRes] = await Promise.all([
        suppliersAPI.get(id),
        scopesAPI.list(),
        filesAPI.list('supplier', id),
        auditAPI.list({ entity_type: 'supplier', entity_id: id, limit: 50 })
      ]);
      setSupplier(supplierRes.data);
      setScopes(scopesRes.data);
      setFiles(filesRes.data);
      setAuditLogs(auditRes.data);
    } catch (err) {
      toast.error('Failed to load supplier');
      navigate('/suppliers/list');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete supplier "${supplier.supplier_name}"? This cannot be undone.`)) return;
    try {
      await suppliersAPI.delete(id);
      toast.success('Supplier deleted');
      navigate('/suppliers/list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const handleStartReevaluation = async () => {
    if (!window.confirm(`Start ${supplier.approval_status === 'Created' ? 'approval' : 'reevaluation'} process for "${supplier.supplier_name}"? This will change their status to "Under re-evaluation".`)) return;
    
    setStartingReeval(true);
    try {
      await suppliersAPI.startReevaluation(id);
      toast.success('Reevaluation started');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start reevaluation');
    } finally {
      setStartingReeval(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'supplier');
      formData.append('entity_id', id);
      formData.append('file_category', 'document');
      await filesAPI.upload(formData);
      toast.success('Document uploaded');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getScopeName = (scopeId) => {
    const scope = scopes.find(s => s.id === scopeId);
    return scope?.name || scopeId;
  };

  const canEdit = isAdmin || (user?.app_roles?.supplier_mgmt === 'manager');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!supplier) return null;

  const isOverdueReeval = supplier.reevaluation_date && new Date(supplier.reevaluation_date) < new Date();
  const canStartReeval = isPurchaseManager && !['Under re-evaluation', 'No Longer Needed'].includes(supplier.approval_status);
  const isInitialApproval = supplier.approval_status === 'Created';
  const canReview = (supplier.reevaluation_status === 'pending_purchase' && isPurchaseManager) ||
                    (supplier.reevaluation_status === 'pending_quality' && isQualityManager);
  const hasRatings = supplier.supplier_rating != null;
  const reevalHistory = supplier.reevaluation_history || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="supplier-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/suppliers/list')}
                className="text-slate-600"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="h-4 w-px bg-slate-300" />
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  <h1 className="text-lg font-semibold text-[#0F172A]">{supplier.supplier_name}</h1>
                  {supplier.supplier_type === 'NON-AS' && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">NON-AS</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">{supplier.supplier_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canReview && supplier.supplier_type !== 'NON-AS' && (
                <Link to={`/suppliers/${id}/reevaluate`}>
                  <Button size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-review-btn">
                    <TrendingUp className="w-4 h-4" /> Submit Review
                  </Button>
                </Link>
              )}
              {canStartReeval && supplier.supplier_type !== 'NON-AS' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartReevaluation}
                  disabled={startingReeval}
                  className="gap-1"
                  data-testid="start-reevaluation-btn"
                >
                  <RefreshCw className="w-4 h-4" /> {startingReeval ? 'Starting...' : isInitialApproval ? 'Start Approval' : 'Start Reevaluation'}
                </Button>
              )}
              {canEdit && (
                <>
                  <Link to={`/suppliers/${id}/edit`}>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="edit-supplier-btn">
                      <Edit className="w-4 h-4" /> Edit
                    </Button>
                  </Link>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="delete-supplier-btn"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <div className={`p-4 rounded-lg border ${STATUS_STYLES[supplier.approval_status]} flex items-center justify-between`} data-testid="status-banner">
          <div className="flex items-center gap-3">
            {supplier.approval_status === 'Approved' ? (
              <CheckCircle className="w-5 h-5" />
            ) : supplier.approval_status === 'Under re-evaluation' ? (
              <Clock className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <div>
              <p className="font-medium">
                Status: {supplier.approval_status}
                {supplier.supplier_type === 'NON-AS' && <span className="ml-2 text-amber-600 text-sm">(General Purpose - NON-AS)</span>}
              </p>
              {supplier.reevaluation_date && supplier.supplier_type !== 'NON-AS' && (
                <p className="text-xs opacity-75">
                  Re-evaluation due: {supplier.reevaluation_date}
                  {isOverdueReeval && ' (OVERDUE)'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {supplier.dpd_mbd_approved && (
              <Badge className="bg-emerald-600 text-white">DPD/MBD Approved</Badge>
            )}
            {supplier.reevaluation_status && supplier.reevaluation_status !== 'completed' && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" data-testid="reeval-stage-badge">
                {supplier.reevaluation_status === 'pending_purchase' ? 'Awaiting Purchase Manager' : 'Awaiting Quality Manager'}
              </Badge>
            )}
          </div>
        </div>

        {/* Past-year performance report */}
        <SupplierPerformanceCard supplierId={id} showQuality={true} />


        {/* Reevaluation Progress (when under reevaluation) */}
        {supplier.approval_status === 'Under re-evaluation' && (
          <Card className="border-blue-200 bg-blue-50/30" data-testid="reevaluation-progress">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                Reevaluation In Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    supplier.purchase_evaluation ? 'bg-emerald-500 text-white' : 
                    supplier.reevaluation_status === 'pending_purchase' ? 'bg-blue-500 text-white animate-pulse' : 'bg-slate-200 text-slate-400'
                  }`}>1</div>
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">Purchase Manager</p>
                    {supplier.purchase_evaluation ? (
                      <p className="text-[10px] text-emerald-600">
                        Done by {supplier.purchase_evaluation.evaluator_name} ({supplier.purchase_evaluation.decision})
                      </p>
                    ) : (
                      <p className="text-[10px] text-blue-600">Pending review</p>
                    )}
                  </div>
                </div>
                <div className="h-px w-8 bg-slate-300" />
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    supplier.quality_evaluation ? 'bg-emerald-500 text-white' : 
                    supplier.reevaluation_status === 'pending_quality' ? 'bg-blue-500 text-white animate-pulse' : 'bg-slate-200 text-slate-400'
                  }`}>2</div>
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">Quality Manager</p>
                    {supplier.quality_evaluation ? (
                      <p className="text-[10px] text-emerald-600">
                        Done by {supplier.quality_evaluation.evaluator_name} ({supplier.quality_evaluation.decision})
                      </p>
                    ) : supplier.reevaluation_status === 'pending_quality' ? (
                      <p className="text-[10px] text-blue-600">Pending review</p>
                    ) : (
                      <p className="text-[10px] text-slate-400">Waiting for Step 1</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Performance Ratings */}
            {hasRatings && (
              <Card data-testid="performance-ratings-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Performance Ratings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                      <p className="text-xs text-emerald-600 font-medium mb-1">Supplier Rating (SR)</p>
                      <p className="text-3xl font-bold text-emerald-700" data-testid="sr-value">{supplier.supplier_rating}</p>
                      <p className="text-[10px] text-emerald-500 mt-1">70% Quality + 30% Delivery</p>
                    </div>
                    <div className="space-y-3 col-span-2">
                      <RatingGauge label="Quality Rating" value={supplier.quality_rating} />
                      <RatingGauge label="Delivery Rating" value={supplier.delivery_rating} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card data-testid="contact-info-card">
              <CardHeader>
                <CardTitle className="text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                  <div>
                    <p className="text-sm">{supplier.address_1}</p>
                    {supplier.address_2 && <p className="text-sm">{supplier.address_2}</p>}
                    <p className="text-sm">{supplier.city}, {supplier.state_province} {supplier.zip_code}</p>
                    <p className="text-sm">{supplier.country}</p>
                  </div>
                </div>
                {supplier.contact_person && (
                  <div className="flex items-center gap-3">
                    <span className="w-4 h-4 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">
                      {supplier.contact_person.charAt(0)}
                    </span>
                    <span className="text-sm">{supplier.contact_person}</span>
                  </div>
                )}
                {supplier.phone_number && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">{supplier.phone_number}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${supplier.email}`} className="text-sm text-blue-600 hover:underline">
                      {supplier.email}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Criteria for Approval */}
            <Card data-testid="criteria-card">
              <CardHeader>
                <CardTitle className="text-base">Criteria for Approval</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {supplier.criteria_quality_cert && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">Quality System Certification</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      Cert: {supplier.quality_cert_number || 'N/A'} | 
                      Expiry: {supplier.quality_cert_expiry || 'N/A'}
                    </p>
                  </div>
                )}
                {supplier.criteria_authorized_distributor && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">Authorized Distributor</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      Ref: {supplier.auth_distributor_cert || 'N/A'} | 
                      Expiry: {supplier.auth_distributor_expiry || 'N/A'}
                    </p>
                  </div>
                )}
                {supplier.criteria_onsite_visit && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">On-site Visit</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      Date: {supplier.onsite_visit_date || 'N/A'} | 
                      By: {supplier.onsite_visit_by || 'N/A'}
                    </p>
                  </div>
                )}
                {supplier.criteria_customer_mandate && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">Customer Mandate</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      {supplier.customer_mandate_details || 'N/A'}
                    </p>
                  </div>
                )}
                {supplier.criteria_sole_source_oem && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">Sole Source / OEM Status</span>
                    </div>
                  </div>
                )}
                {supplier.criteria_sample_approval && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium text-sm">Sample Approval</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 ml-6">
                      PO: {supplier.sample_po_number || 'N/A'} | 
                      Date: {supplier.sample_po_date || 'N/A'}
                    </p>
                  </div>
                )}
                {!supplier.criteria_quality_cert && !supplier.criteria_authorized_distributor && 
                 !supplier.criteria_onsite_visit && !supplier.criteria_customer_mandate &&
                 !supplier.criteria_sole_source_oem && !supplier.criteria_sample_approval && (
                  <p className="text-sm text-slate-400 text-center py-4">No approval criteria selected</p>
                )}
                {supplier.criteria_approval_text && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-1">Additional Notes</p>
                    <p className="text-sm text-[#0F172A]" data-testid="criteria-approval-text-display">{supplier.criteria_approval_text}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reevaluation History */}
            {reevalHistory.length > 0 && (
              <Card data-testid="reevaluation-history-card">
                <CardHeader className="cursor-pointer" onClick={() => setHistoryExpanded(!historyExpanded)}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      Reevaluation History ({reevalHistory.length})
                    </CardTitle>
                    {historyExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </CardHeader>
                {historyExpanded && (
                  <CardContent className="space-y-4">
                    {reevalHistory.slice().reverse().map((entry, idx) => (
                      <div key={entry.id || idx} className="p-4 border border-slate-200 rounded-lg" data-testid={`history-entry-${idx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={
                              entry.final_status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              entry.final_status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-red-50 text-red-700 border-red-200'
                            }>
                              {entry.final_status}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {entry.evaluated_at ? new Date(entry.evaluated_at).toLocaleDateString() : '-'}
                            </span>
                          </div>
                          {entry.supplier_rating != null && (
                            <span className="text-sm font-bold text-emerald-700">SR: {entry.supplier_rating}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs mb-3">
                          <div>
                            <span className="text-slate-400">Quality Rating</span>
                            <p className="font-semibold">{entry.quality_rating != null ? entry.quality_rating : '-'}</p>
                          </div>
                          <div>
                            <span className="text-slate-400">Delivery Rating</span>
                            <p className="font-semibold">{entry.delivery_rating != null ? entry.delivery_rating : '-'}</p>
                          </div>
                          <div>
                            <span className="text-slate-400">Evaluated By</span>
                            <p className="font-semibold">
                              {entry.quality_evaluation?.evaluator_name || entry.purchase_evaluation?.evaluator_name || '-'}
                            </p>
                          </div>
                        </div>

                        {/* Purchase Manager Review */}
                        {entry.purchase_evaluation && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Purchase Manager Review — {entry.purchase_evaluation.evaluator_name}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                              {entry.purchase_evaluation.risk_assessment && (
                                <div><span className="text-slate-400">Risk Assessment:</span> <span className="text-slate-700">{entry.purchase_evaluation.risk_assessment}</span></div>
                              )}
                              {entry.purchase_evaluation.production_capability && (
                                <div><span className="text-slate-400">Production:</span> <span className="text-slate-700">{entry.purchase_evaluation.production_capability}</span></div>
                              )}
                              {entry.purchase_evaluation.financial_capability && (
                                <div><span className="text-slate-400">Financial:</span> <span className="text-slate-700">{entry.purchase_evaluation.financial_capability}</span></div>
                              )}
                              {entry.purchase_evaluation.lead_time && (
                                <div><span className="text-slate-400">Lead Time:</span> <span className="text-slate-700">{entry.purchase_evaluation.lead_time}</span></div>
                              )}
                              {entry.purchase_evaluation.counterfeit_prevention && (
                                <div><span className="text-slate-400">Counterfeit Prevention:</span> <span className="text-slate-700">{entry.purchase_evaluation.counterfeit_prevention}</span></div>
                              )}
                              <RiskAssessmentRows ev={entry.purchase_evaluation} />
                              <div><span className="text-slate-400">Decision:</span> <Badge variant="outline" className="text-[10px] ml-1">{entry.purchase_evaluation.decision?.replace('_', ' ')}</Badge></div>
                            </div>
                          </div>
                        )}

                        {/* Quality Manager Review */}
                        {entry.quality_evaluation && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Quality Manager Review — {entry.quality_evaluation.evaluator_name}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                              {entry.quality_evaluation.risk_assessment && (
                                <div><span className="text-slate-400">Risk Assessment:</span> <span className="text-slate-700">{entry.quality_evaluation.risk_assessment}</span></div>
                              )}
                              {entry.quality_evaluation.production_capability && (
                                <div><span className="text-slate-400">Production:</span> <span className="text-slate-700">{entry.quality_evaluation.production_capability}</span></div>
                              )}
                              {entry.quality_evaluation.financial_capability && (
                                <div><span className="text-slate-400">Financial:</span> <span className="text-slate-700">{entry.quality_evaluation.financial_capability}</span></div>
                              )}
                              {entry.quality_evaluation.lead_time && (
                                <div><span className="text-slate-400">Lead Time:</span> <span className="text-slate-700">{entry.quality_evaluation.lead_time}</span></div>
                              )}
                              {entry.quality_evaluation.counterfeit_prevention && (
                                <div><span className="text-slate-400">Counterfeit Prevention:</span> <span className="text-slate-700">{entry.quality_evaluation.counterfeit_prevention}</span></div>
                              )}
                              <RiskAssessmentRows ev={entry.quality_evaluation} />
                              <div><span className="text-slate-400">Decision:</span> <Badge variant="outline" className="text-[10px] ml-1">{entry.quality_evaluation.decision?.replace('_', ' ')}</Badge></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            )}

            {/* Change Log */}
            <Card data-testid="change-log-card">
              <CardHeader className="cursor-pointer" onClick={() => setChangeLogExpanded(!changeLogExpanded)}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="w-4 h-4 text-slate-500" />
                    Change Log ({auditLogs.length})
                  </CardTitle>
                  {changeLogExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </CardHeader>
              {changeLogExpanded && (
                <CardContent>
                  {auditLogs.length > 0 ? (
                    <div className="space-y-3">
                      {auditLogs.map((log, idx) => (
                        <div key={log.id || idx} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg" data-testid={`changelog-entry-${idx}`}>
                          <div className="mt-0.5">
                            <Badge
                              variant="outline"
                              className={`text-[10px] font-semibold ${
                                log.action === 'CREATE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                log.action === 'UPDATE' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                log.action === 'DELETE' ? 'bg-red-50 text-red-700 border-red-200' :
                                log.action === 'UPLOAD' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                log.action === 'IMPORT' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-600 border-slate-200'
                              }`}
                            >
                              {log.action}
                            </Badge>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-[#0F172A] break-words">{log.details}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-slate-400">{log.user_name}</span>
                              <span className="text-[10px] text-slate-300">|</span>
                              <span className="text-[10px] text-slate-400">
                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No change history</p>
                  )}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card data-testid="quick-info-card">
              <CardHeader>
                <CardTitle className="text-base">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">SAP Code</span>
                  <span className="font-mono">{supplier.supplier_code || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Date of Approval</span>
                  <span>{supplier.date_of_approval || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Created</span>
                  <span>{supplier.created_at?.substring(0, 10)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Created By</span>
                  <span>{supplier.created_by_name || '-'}</span>
                </div>
                {supplier.reevaluation_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Next Reevaluation</span>
                    <span className={isOverdueReeval ? 'text-red-600 font-medium' : ''}>
                      {supplier.reevaluation_date}
                      {isOverdueReeval && ' (Overdue)'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scope of Approval */}
            <Card data-testid="scope-card">
              <CardHeader>
                <CardTitle className="text-base">Scope of Approval</CardTitle>
              </CardHeader>
              <CardContent>
                {supplier.scope_of_approval?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {supplier.scope_of_approval.map(scopeId => (
                      <Badge key={scopeId} variant="outline" className="text-xs">
                        {getScopeName(scopeId)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No scopes assigned</p>
                )}
                {supplier.scope_of_approval_text && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-1">Additional Notes</p>
                    <p className="text-sm text-[#0F172A]" data-testid="scope-approval-text-display">{supplier.scope_of_approval_text}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card data-testid="documents-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Documents</CardTitle>
                {canEdit && (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      data-testid="supplier-file-upload"
                    />
                    <Button variant="ghost" size="sm" className="text-xs gap-1" asChild>
                      <span>
                        <Upload className="w-3 h-3" />
                        {uploading ? 'Uploading...' : 'Upload'}
                      </span>
                    </Button>
                  </label>
                )}
              </CardHeader>
              <CardContent>
                {files.length > 0 ? (
                  <div className="space-y-2">
                    {files.map(file => {
                      const isCurrent = file.doc_status === 'current';
                      return (
                        <div
                          key={file.id}
                          className={`flex items-center gap-2 p-2 rounded text-sm ${isCurrent ? 'hover:bg-slate-50' : 'opacity-50 bg-slate-50'}`}
                          data-testid={`supplier-file-${file.id}`}
                        >
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <a
                            href={filesAPI.getUrl(file.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 truncate hover:underline"
                          >
                            {file.filename}
                          </a>
                          <Badge
                            variant="outline"
                            className={`text-[10px] flex-shrink-0 ${isCurrent ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                          >
                            {isCurrent ? 'Current' : 'Obsolete'}
                          </Badge>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-[10px] flex-shrink-0"
                              onClick={async () => {
                                try {
                                  await filesAPI.toggleStatus(file.id);
                                  toast.success(`Marked as ${isCurrent ? 'obsolete' : 'current'}`);
                                  loadData();
                                } catch (err) {
                                  toast.error('Failed to update status');
                                }
                              }}
                              data-testid={`toggle-status-${file.id}`}
                            >
                              {isCurrent ? <XCircle className="w-3 h-3 mr-0.5" /> : <CheckCircle className="w-3 h-3 mr-0.5" />}
                              {isCurrent ? 'Mark Obsolete' : 'Mark Current'}
                            </Button>
                          )}
                          <a href={filesAPI.getUrl(file.id)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                            <Download className="w-3 h-3 text-slate-400" />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No documents uploaded</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
